import { BodyText, Button, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import { FileText } from "lucide-react";
import { Document, Page } from "react-pdf";
import { type UsePdfViewerOptions, usePdfViewer } from "./usePdfViewer";

export interface PdfViewerProps extends UsePdfViewerOptions {
  className?: string;
  showZoomControls?: boolean;
  showPageCount?: boolean;
  showOpenInNewTab?: boolean;
  containerClassName?: string;
}

export const PdfViewer = ({
  src,
  className,
  showZoomControls = true,
  showPageCount = true,
  showOpenInNewTab = true,
  containerClassName,
  ...usePdfViewerOptions
}: PdfViewerProps) => {
  const {
    numPages,
    scale,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    openInNewTab,
    zoomIn,
    zoomOut,
    resetZoom,
  } = usePdfViewer({ src, ...usePdfViewerOptions });

  const renderAllPages = () => {
    if (!numPages) return null;

    const pages = [];
    for (let i = 1; i <= numPages; i++) {
      pages.push(
        <Page
          key={`page_${i}`}
          className="mb-4 shadow-lg"
          pageNumber={i}
          scale={scale}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          loading={<Skeleton className="w-full h-96 mb-4" />}
        />,
      );
    }
    return pages;
  };

  if (!src) return null;

  return (
    <div className={cn("flex flex-col w-full h-full", containerClassName)}>
      {/* Controls Header */}
      {(showPageCount || showOpenInNewTab || showZoomControls) && (
        <div className="flex justify-between items-center mb-4 px-2">
          {showPageCount && (
            <BodyText size="sm" textColor="secondary">
              {numPages
                ? `${numPages} page${numPages > 1 ? "s" : ""}`
                : "Loading..."}
            </BodyText>
          )}

          <div className="flex gap-2">
            {showZoomControls && (
              <>
                <Button variant="primary" size="xs" onClick={zoomOut}>
                  <BodyText
                    size="sm"
                    textColor="white"
                    className="cursor-pointer"
                  >
                    Zoom Out
                  </BodyText>
                </Button>
                <Button variant="primary" size="xs" onClick={zoomIn}>
                  <BodyText
                    size="sm"
                    textColor="white"
                    className="cursor-pointer"
                  >
                    Zoom In
                  </BodyText>
                </Button>
                <Button variant="primary" size="xs" onClick={resetZoom}>
                  <BodyText
                    size="sm"
                    textColor="white"
                    className="cursor-pointer"
                  >
                    Reset Zoom
                  </BodyText>
                </Button>
              </>
            )}

            {showOpenInNewTab && (
              <Button variant="secondary" size="sm" onClick={openInNewTab}>
                Open in New Tab
              </Button>
            )}
          </div>
        </div>
      )}

      {/* PDF Content */}
      <div
        className={cn(
          "flex-1 bg-tertiary rounded-lg overflow-auto p-4",
          className,
        )}
      >
        <Document
          file={src}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={<Skeleton className="w-full h-96" />}
          error={
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <FileText className="w-16 h-16 text-muted-foreground mb-4" />
              <BodyText size="lg" textColor="secondary" className="mb-2">
                Error loading PDF
              </BodyText>
              <BodyText size="sm" textColor="secondary">
                This document could not be displayed. Try opening it in a new
                tab.
              </BodyText>
              {showOpenInNewTab && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={openInNewTab}
                  className="mt-4"
                >
                  Open in New Tab
                </Button>
              )}
            </div>
          }
          className="flex flex-col items-center"
        >
          {renderAllPages()}
        </Document>
      </div>
    </div>
  );
};
